import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Plus, Minus, Search, Heart, User, Check, Loader2, Truck, RefreshCw, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";
import { shopProducts, shopCheckout, shopOrderStatus } from "@/lib/api";
import { toast } from "sonner";

const TAUPE = "#8A7B6B";
const DARK = "#3A342D";
const CREAM = "#FBF8F3";
const serif = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const CART_KEY = "elara-cart";
const money = (cents) => `$${(cents / 100).toFixed(0)}`;

const CATEGORIES = [
  { name: "Clothing", img: "https://images.unsplash.com/photo-1759229874914-c1ffdb3ebd0c?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0" },
  { name: "Dresses", img: "https://images.unsplash.com/photo-1632282005753-29f80ed13c93?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0" },
  { name: "Accessories", img: "https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0" },
  { name: "Shoes", img: "https://images.unsplash.com/photo-1648066380643-675d30c3cd17?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0" },
];
const TRUST = [
  { icon: Truck, t: "Complimentary shipping over $100" },
  { icon: RefreshCw, t: "Easy 30-day returns" },
  { icon: ShieldCheck, t: "Secure checkout" },
  { icon: Sparkles, t: "Thoughtfully made" },
];

function ProductCard({ p, onAdd }) {
  const [size, setSize] = useState(p.sizes?.[0] || "One Size");
  return (
    <div className="group" data-testid={`elara-product-${p.id}`}>
      <div className="relative overflow-hidden rounded-lg bg-white aspect-[3/4]">
        <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <button onClick={() => onAdd(p, size)} data-testid={`elara-quickadd-${p.id}`}
          className="absolute bottom-3 left-3 right-3 rounded-full py-2.5 text-xs tracking-[0.15em] uppercase font-semibold bg-white/95 backdrop-blur opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" style={{ color: DARK }}>
          Quick Add
        </button>
      </div>
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium" style={{ color: DARK }}>{p.name}</span>
          <span className="text-sm" style={{ color: TAUPE }}>{money(p.price)}</span>
        </div>
        {p.sizes && p.sizes.length > 1 && (
          <div className="flex gap-1.5 mt-2">
            {p.sizes.map((s) => (
              <button key={s} onClick={() => setSize(s)} data-testid={`elara-size-${p.id}-${s}`}
                className={`text-[11px] h-7 min-w-7 px-2 rounded-full border transition-colors ${size === s ? "text-white" : ""}`}
                style={size === s ? { background: DARK, borderColor: DARK } : { borderColor: "#dcd4c8", color: DARK }}>{s}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ElaraDemo() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; } });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [orderDone, setOrderDone] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => { window.scrollTo(0, 0); document.title = "ELARA & IVY (Demo) | WebsiteTech.pro"; shopProducts("elara").then((d) => setProducts(d.products)).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  // Post-payment return handling
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("paid") === "1" && sid) {
      let tries = 0;
      const poll = async () => {
        try {
          const d = await shopOrderStatus(sid);
          if (d.payment_status === "paid") { setOrderDone(d); setCart([]); localStorage.removeItem(CART_KEY); setParams({}, { replace: true }); return; }
        } catch { /* ignore */ }
        if (tries++ < 15) setTimeout(poll, 1500);
        else {
          // Payment likely succeeded but confirmation is slow — reassure the shopper.
          setCart([]); localStorage.removeItem(CART_KEY); setParams({}, { replace: true });
          toast.success("Payment received — your order confirmation is on its way.");
        }
      };
      poll();
    } else if (params.get("canceled") === "1") {
      toast("Checkout canceled — your bag is saved.");
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const addToCart = useCallback((p, size) => {
    setCart((c) => {
      const key = `${p.id}__${size}`;
      const found = c.find((i) => i.key === key);
      if (found) return c.map((i) => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { key, id: p.id, name: p.name, price: p.price, image: p.image, size, qty: 1 }];
    });
    setCartOpen(true);
    toast.success(`${p.name} added to bag`);
  }, []);
  const setQty = (key, delta) => setCart((c) => c.flatMap((i) => i.key === key ? (i.qty + delta <= 0 ? [] : [{ ...i, qty: i.qty + delta }]) : [i]));
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const count = cart.reduce((s, i) => s + i.qty, 0);

  const checkout = async () => {
    if (!cart.length) return;
    setCheckingOut(true);
    try {
      const res = await shopCheckout({ store: "elara", items: cart.map((i) => ({ id: i.id, qty: i.qty, size: i.size })), origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error("Could not start checkout. Please try again."); setCheckingOut(false); }
  };

  const newArrivals = products.filter((p) => p.category === "New Arrivals");
  const accessories = products.filter((p) => p.category === "Accessories");

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: DARK, ...body }} data-testid="fashion-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: DARK }}>
        WebsiteTech.pro Demo · Fashion E-commerce — click to view all industry demos
      </Link>
      <div className="text-center text-[11px] tracking-[0.2em] uppercase py-2" style={{ background: "#EFE8DD", color: TAUPE }}>Complimentary shipping over $100</div>

      <DemoTopNav
        testid="fashion-nav"
        logo={<div className="text-xl tracking-[0.28em]" style={{ ...serif, color: DARK }}>ELARA &amp; IVY</div>}
        items={["New", "Clothing", "Dresses", "Accessories", "Collections", "Our Story"]}
        ctaLabel={count ? `Bag · ${count}` : "Bag"}
        onCta={() => setCartOpen(true)}
        theme={{ bg: "rgba(251,248,243,0.94)", text: DARK, accent: DARK, accentText: "#fff", border: "#e6ded2" }}
      />

      {/* hero */}
      <header data-testid="fashion-hero" className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6 items-center py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <span className="text-sm italic" style={{ ...serif, color: TAUPE }}>Style lives brighter here</span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={serif}>Quiet confidence,<br />beautifully made.</h1>
          <p className="mt-5 text-base opacity-70 max-w-md">Considered essentials in warm neutrals — designed to be worn, loved, and kept.</p>
          <div className="mt-8 flex gap-3">
            <a href="#new" className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white flex items-center gap-2" style={{ background: DARK }}>Shop New Arrivals <ArrowRight size={14} /></a>
            <a href="#accessories" className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold" style={{ border: `1px solid ${TAUPE}66`, color: DARK }}>The Autumn Edit</a>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="grid grid-cols-2 gap-4">
          <img src="https://images.unsplash.com/photo-1618333453296-9e35280fd6b1?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0" alt="" className="rounded-xl h-[360px] w-full object-cover mt-8" />
          <img src="https://images.unsplash.com/photo-1648066380643-675d30c3cd17?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0" alt="" className="rounded-xl h-[360px] w-full object-cover" />
        </motion.div>
      </header>

      {/* categories */}
      <section data-testid="fashion-categories" className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {CATEGORIES.map((c) => (
          <div key={c.name} className="relative rounded-xl overflow-hidden aspect-[4/5] group cursor-pointer">
            <img src={c.img} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 flex items-end p-4" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.4), transparent 60%)" }}>
              <span className="text-white text-sm tracking-[0.2em] uppercase font-semibold">{c.name}</span>
            </div>
          </div>
        ))}
      </section>

      {/* new arrivals */}
      <section id="new" data-testid="fashion-new-arrivals" className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-8"><h2 className="text-3xl" style={serif}>New Arrivals</h2><span className="text-xs tracking-widest uppercase" style={{ color: TAUPE }}>View All →</span></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {newArrivals.map((p) => <ProductCard key={p.id} p={p} onAdd={addToCart} />)}
        </div>
      </section>

      {/* autumn edit banner */}
      <section className="relative">
        <img src="https://images.unsplash.com/photo-1618333453296-9e35280fd6b1?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80&ixlib=rb-4.1.0" alt="" className="h-[320px] w-full object-cover" />
        <div className="absolute inset-0 flex items-center" style={{ background: "linear-gradient(90deg, rgba(58,52,45,0.72), rgba(58,52,45,0.15))" }}>
          <div className="max-w-7xl mx-auto px-6 w-full text-white">
            <span className="text-sm italic" style={serif}>Now in season</span>
            <h2 className="mt-2 text-4xl md:text-5xl" style={serif}>The Autumn Edit</h2>
            <p className="mt-3 max-w-md opacity-85">Elevated knitwear and warm neutrals for the softer months ahead.</p>
            <button className="mt-6 rounded-full px-7 py-3 text-xs tracking-[0.18em] uppercase font-semibold bg-white" style={{ color: DARK }}>Explore the Edit</button>
          </div>
        </div>
      </section>

      {/* accessories */}
      <section id="accessories" data-testid="fashion-accessories" className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-8"><h2 className="text-3xl" style={serif}>Bestselling Accessories</h2><span className="text-xs tracking-widest uppercase" style={{ color: TAUPE }}>View All →</span></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {accessories.map((p) => <ProductCard key={p.id} p={p} onAdd={addToCart} />)}
        </div>
      </section>

      {/* testimonial */}
      <section className="py-16 text-center" style={{ background: "#EFE8DD" }}>
        <p className="max-w-2xl mx-auto px-6 text-2xl md:text-3xl leading-relaxed" style={serif}>"Everything I've bought from ELARA &amp; IVY has become a favorite. The quality is remarkable and the fit is perfect."</p>
        <div className="mt-4 text-sm" style={{ color: TAUPE }}>— Maren L., verified customer</div>
      </section>

      {/* trust */}
      <section className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
        {TRUST.map((t) => (
          <div key={t.t} className="flex flex-col items-center text-center gap-2"><t.icon size={22} style={{ color: TAUPE }} /><span className="text-sm opacity-70 max-w-[170px]">{t.t}</span></div>
        ))}
      </section>

      {/* newsletter */}
      <section className="py-14" style={{ background: DARK }}>
        <div className="max-w-xl mx-auto px-6 text-center text-white">
          <h3 className="text-2xl" style={serif}>Join the ELARA &amp; IVY list</h3>
          <p className="mt-2 text-sm opacity-70">Early access to new arrivals and seasonal edits.</p>
          <div className="mt-5 flex gap-2">
            <input placeholder="Your email" className="flex-1 rounded-full px-5 py-3 text-sm bg-white/10 border border-white/20 outline-none placeholder:text-white/50" />
            <button className="rounded-full px-6 py-3 text-xs tracking-widest uppercase font-semibold bg-white" style={{ color: DARK }}>Subscribe</button>
          </div>
        </div>
      </section>

      <footer data-testid="fashion-footer" className="text-center py-10 text-xs tracking-widest uppercase" style={{ color: TAUPE }}>
        ELARA &amp; IVY · Clothing · Dresses · Accessories · Our Story
      </footer>

      {/* cart drawer */}
      <AnimatePresence>
        {cartOpen && (
          <div className="fixed inset-0 z-[80]" data-testid="fashion-cart-drawer">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 260 }}
              className="absolute right-0 top-0 h-full w-full sm:w-[400px] bg-white flex flex-col" style={body}>
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#eee" }}>
                <span className="text-sm tracking-[0.18em] uppercase font-semibold">Your Bag {count ? `(${count})` : ""}</span>
                <button onClick={() => setCartOpen(false)} data-testid="fashion-cart-close"><X size={18} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {cart.length === 0 ? (
                  <div className="text-center text-slate-400 py-20 text-sm" data-testid="fashion-cart-empty"><ShoppingBag className="mx-auto mb-3" size={28} />Your bag is empty.</div>
                ) : cart.map((i) => (
                  <div key={i.key} className="flex gap-3" data-testid="fashion-cart-item">
                    <img src={i.image} alt={i.name} className="h-20 w-16 rounded object-cover" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{i.name}</div>
                      {i.size && i.size !== "One Size" && <div className="text-xs text-slate-400">Size {i.size}</div>}
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => setQty(i.key, -1)} className="h-6 w-6 grid place-items-center rounded border" style={{ borderColor: "#ddd" }}><Minus size={12} /></button>
                        <span className="text-sm w-5 text-center">{i.qty}</span>
                        <button onClick={() => setQty(i.key, 1)} className="h-6 w-6 grid place-items-center rounded border" style={{ borderColor: "#ddd" }}><Plus size={12} /></button>
                      </div>
                    </div>
                    <div className="text-sm" style={{ color: TAUPE }}>{money(i.price * i.qty)}</div>
                  </div>
                ))}
              </div>
              {cart.length > 0 && (
                <div className="border-t p-5 space-y-3" style={{ borderColor: "#eee" }}>
                  <div className="flex justify-between text-sm"><span className="opacity-60">Subtotal</span><span className="font-semibold">{money(subtotal)}</span></div>
                  <button onClick={checkout} disabled={checkingOut} data-testid="fashion-checkout-btn"
                    className="w-full rounded-full py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: DARK }}>
                    {checkingOut ? <Loader2 size={15} className="animate-spin" /> : null} Checkout · {money(subtotal)}
                  </button>
                  <p className="text-[11px] text-slate-400 text-center flex items-center justify-center gap-1"><ShieldCheck size={12} /> Secure checkout powered by Stripe</p>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* order confirmation */}
      {orderDone && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setOrderDone(null)} data-testid="fashion-order-success">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-8 max-w-sm text-center" onClick={(e) => e.stopPropagation()} style={body}>
            <div className="w-14 h-14 rounded-full mx-auto grid place-items-center" style={{ background: `${TAUPE}22`, color: TAUPE }}><Check size={26} /></div>
            <h3 className="mt-5 text-2xl" style={serif}>Order confirmed</h3>
            <p className="mt-2 text-sm text-slate-500">Thank you for shopping ELARA &amp; IVY. A confirmation is on its way — your pieces will ship shortly.</p>
            <div className="mt-4 text-sm font-semibold">Total paid: {money(orderDone.amount || 0)}</div>
            <button onClick={() => setOrderDone(null)} className="mt-6 rounded-full px-8 py-3 text-xs tracking-widest uppercase font-semibold text-white" style={{ background: DARK }}>Continue Shopping</button>
          </motion.div>
        </div>
      )}

      <AIConciergeWidget demo="fashion" concierge={DEMO_THEMES.fashion.concierge} />
    </div>
  );
}
